import { DEFAULT_LANGUAGE } from '@constants'
import {
  CLOSED_KEY,
  DUPLICATE_OF_KEY,
  PUBLISHED_KEY,
  SHOULD_BE_APPROVED_KEY,
  SHOULD_CREATE_INLINE_IMAGES_KEY,
  SUSPENDED_KEY,
} from '@constants/fieldKeys'
import {
  ContentItemTranslationType,
  ContentItemV2,
  ContextType,
  FieldType,
  ResourceType,
  StringItemStatus,
  StringItemTranslationType,
  TemplateType,
} from '@generated/graphql'
import { getPropertyValue } from '@modules/property'
import { TemplateField } from '@store/slices/templates/types'
import { getContextId, getFieldByKey, hasDraftString } from '@utils'
import get from 'lodash/get'

import {
  CONNECTION_RESOURCES_KEY,
  CONTENT_VARIANT_AFFIX,
  CONTENT_VARIANT_ERROR_TEXT,
  FALLBACK_AFFIX,
  FALLBACK_ERROR_TEXT,
  // TEMP_OLD_MARKDOWN_TYPe_PORTABLE_TEXT,
} from './constants'
import {
  IAddContentVariantAffix,
  IFilterResourceContentByLanguage,
  IGetFieldByFormKey,
  IGetFieldValueFromForm,
  IGetFieldValueFromFormReturn,
  IGetPropertyValueOfName,
  IGraphqlDataToInitialValues,
  IGraphqlDataToInitialValuesReturn,
  IPrepareContent,
  IPrepareContentReturn,
  IPrepareProperties,
  IPreparePropertiesReturn,
  IValuesToGraphqlData,
  IValuesToGraphqlDataReturn,
} from './types'

const generateFields = ({
  acc,
  resourceContentOrProperty,
  template,
  data,
  currentNetwork,
  currentVertical,
  currentSite,
  currentLanguageTag,
}) => {
  const field = getFieldByKey({
    key: resourceContentOrProperty.key,
    fields: template.fields,
  })
  if (field) {
    // only unique keys allowed
    if (acc[resourceContentOrProperty.key]) {
      return acc
    }

    try {
      if (field.resourceType === ResourceType.Property) {
        if (field.context === ContextType.Vertical) {
          // fallback functionality
          const propertiesByKey = [...(data.properties || [])].filter(
            p => p.key === resourceContentOrProperty.key,
          )
          const fallbackProperty = propertiesByKey.find(
            p => p.contextId === currentNetwork?.id,
          )
          const currentContextProperty = propertiesByKey.find(
            p => p.contextId === currentVertical?.id,
          )
          const currentContextPropertyValue =
            currentContextProperty?.data?.value
          const fallbackPropertyValue = fallbackProperty?.data?.value
          if (currentContextProperty) {
            if (typeof currentContextPropertyValue !== 'undefined') {
              acc[currentContextProperty.key] = currentContextPropertyValue
            }
          }

          if (fallbackProperty) {
            if (typeof fallbackPropertyValue !== 'undefined') {
              acc[addFallbackAffix(fallbackProperty.key)] =
                fallbackPropertyValue
            }
          }
        } else {
          const contextId = getContextId({
            field,
            currentNetwork,
            currentVertical,
            currentSite,
          })
          const propertiesByKey = [...(data.properties || [])].filter(
            p => p.key === resourceContentOrProperty.key,
          )
          const currentContextProperty =
            propertiesByKey.find(p => p.contextId === contextId) ||
            resourceContentOrProperty
          const currentContextPropertyValue =
            currentContextProperty?.data?.value
          if (typeof currentContextPropertyValue !== 'undefined') {
            acc[resourceContentOrProperty.key] = currentContextPropertyValue
          }
        }
      } else if (field.resourceType === ResourceType.Content) {
        const strings = [...(resourceContentOrProperty.strings || [])].filter(
          string => {
            // handling notranslate type of fields
            return field.defaultTranslationType ===
              ContentItemTranslationType.Notranslate
              ? string.language.languageTag === DEFAULT_LANGUAGE.languageTag
              : string.language.languageTag === currentLanguageTag
          },
        )

        if (field.data?.contentVariant) {
          // handle content field with content variants
          strings.forEach(string => {
            const variantKey = addContentVariantAffix({
              key: resourceContentOrProperty.key,
              variant: string.variant,
            })

            // Draft strings feature handling. Strings are sorted and first one is always actual
            if (acc[variantKey]) return
            acc[variantKey] = string.data
          })
        } else {
          // handle common content field

          // Draft strings feature handling. Strings are sorted and first one is always actual
          const baseVariantStringItem = strings[0]
          if (baseVariantStringItem) {
            acc[resourceContentOrProperty.key] = baseVariantStringItem.data
          }
        }
      }
    } catch (e) {
      throw new Error(
        `Editor: Unable to generate property or content with key ${field.key} and type: ${field.type}`,
      )
    }
  }

  return acc
}

export const generateFieldsV2 = ({
  items,
  template,
  data,
  currentNetwork,
  currentVertical,
  currentSite,
  currentLanguageTag,
}) => {
  if (!Array.isArray(items) || items.length === 0) return {}

  // Group items by key
  const groupedItems = items.reduce((acc, item) => {
    if (!acc[item.key]) acc[item.key] = []
    acc[item.key].push(item)
    return acc
  }, {})

  const result = {}

  for (const key of Object.keys(groupedItems)) {
    const field = getFieldByKey({ key, fields: template.fields })

    if (!field) continue
    const items = groupedItems[key]

    try {
      if (field.resourceType === ResourceType.Property) {
        if (field.context === ContextType.Vertical) {
          // fallback functionality
          const propertiesByKey = [...(data.properties || [])].filter(
            p => p.key === key,
          )
          const fallbackProperty = propertiesByKey.find(
            p => p.contextId === currentNetwork?.id,
          )
          const currentContextProperty = propertiesByKey.find(
            p => p.contextId === currentVertical?.id,
          )
          const currentContextPropertyValue =
            currentContextProperty?.data?.value
          const fallbackPropertyValue = fallbackProperty?.data?.value
          if (currentContextProperty) {
            if (typeof currentContextPropertyValue !== 'undefined') {
              result[currentContextProperty.key] = currentContextPropertyValue
            }
          }
          if (fallbackProperty) {
            if (typeof fallbackPropertyValue !== 'undefined') {
              result[addFallbackAffix(fallbackProperty.key)] =
                fallbackPropertyValue
            }
          }
        } else {
          const contextId = getContextId({
            field,
            currentNetwork,
            currentVertical,
            currentSite,
          })
          const currentContextItem =
            items.find(item => item.contextId === contextId) || items[0]
          const currentContextValue = currentContextItem?.data?.value
          if (typeof currentContextValue !== 'undefined') {
            result[key] = currentContextValue
          }
        }
      } else if (field.resourceType === ResourceType.Content) {
        const isNoTranslate =
          field.defaultTranslationType ===
          ContentItemTranslationType.Notranslate

        // Only process items for the correct language (approved and draft)
        const itemsForLanguage = items.filter((contentItem: ContentItemV2) => {
          return isNoTranslate
            ? contentItem.language?.languageTag === DEFAULT_LANGUAGE.languageTag
            : contentItem.language.languageTag === currentLanguageTag
        })

        if (!itemsForLanguage.length) continue
        if (field.data?.contentVariant) {
          // TODO: check if needed for contentVariant fields; aggregated by variant
          // Draft strings feature handling. Strings are sorted and first one is always actual
          itemsForLanguage.forEach((item: ContentItemV2) => {
            const variantKey = addContentVariantAffix({
              key,
              variant: item.variant,
            })

            if (!result[variantKey]) {
              result[variantKey] = item.data
            }
          })
        } else {
          // Draft strings feature handling. Strings are sorted and first one is always actual
          const baseVariantContentItem = itemsForLanguage[0]
          if (baseVariantContentItem) {
            result[key] = baseVariantContentItem.data
          }
        }
      }
    } catch (e) {
      throw new Error(
        `Editor: Unable to generate property or content (V2) with key ${field.key} and type: ${field.type}`,
      )
    }
  }
  return result
}

export const graphqlDataToInitialValues = ({
  data,
  template,
  currentLanguageTag,
  currentNetwork,
  currentVertical,
  currentSite,
  connectionResourcesData,
}: IGraphqlDataToInitialValues): IGraphqlDataToInitialValuesReturn => {
  if (!data || !template?.fields) return {}

  const resourceContentOrProperties = [
    ...(data.content || []),
    ...(data.properties || []),
  ]

  let mainFields = {}

  const isContentItemV2 = !data.content?.[0]?.strings

  // generating fields for the main resource
  if (isContentItemV2) {
    // new
    mainFields = generateFieldsV2({
      items: resourceContentOrProperties,
      template,
      data,
      currentLanguageTag,
      currentNetwork,
      currentVertical,
      currentSite,
    })
  } else {
    // old
    mainFields = resourceContentOrProperties.reduce(
      (acc, resourceContentOrProperty) => {
        return generateFields({
          acc,
          resourceContentOrProperty,
          data,
          template,
          currentLanguageTag,
          currentNetwork,
          currentVertical,
          currentSite,
        })
      },
      {},
    )
  }

  const propertyTemplateFieldsWithDataDefault = template.fields.filter(
    templateField =>
      !Object.keys(mainFields).includes(templateField.key) &&
      templateField.resourceType === ResourceType.Property &&
      templateField.data?.default,
  )

  propertyTemplateFieldsWithDataDefault.forEach(templateField => {
    mainFields[templateField.key] = templateField.data.default
  })

  // generating fields for the connection-type subresources
  let connectionResourceFields = {
    [CONNECTION_RESOURCES_KEY]: {},
  }
  if (connectionResourcesData) {
    for (const connectionResourceData of connectionResourcesData) {
      const {
        resources: connectionResources = [],
        template: connectionTemplate,
      } = connectionResourceData || {}

      if (!connectionTemplate) continue
      connectionResourceFields[CONNECTION_RESOURCES_KEY] = {
        ...connectionResourceFields[CONNECTION_RESOURCES_KEY],
        [connectionTemplate.type]: {},
      }

      for (const connectionResource of connectionResources) {
        const connectionResourceContentOrProperties = [
          ...(connectionResource?.content || []),
          ...(connectionResource?.properties || []),
        ]

        const fields =
          connectionTemplate && connectionResource
            ? generateFieldsV2({
                items: connectionResourceContentOrProperties,
                data: connectionResource,
                template: connectionTemplate,
                currentLanguageTag,
                currentNetwork,
                currentVertical,
                currentSite,
              })
            : []

        let connectedResourceId = null
        switch (connectionTemplate.type) {
          case TemplateType.Providertaxonomyconnection:
            connectedResourceId =
              connectionResource?.taxonomyItem?.id ||
              connectionResource?.taxonomyItemId
            break
          default:
            connectedResourceId = null
            break
        }

        connectionResourceFields[CONNECTION_RESOURCES_KEY][
          connectionTemplate.type
        ] = {
          ...connectionResourceFields[CONNECTION_RESOURCES_KEY][
            connectionTemplate.type
          ],
          [connectedResourceId]: { ...fields },
        }
      }
    }
  }

  return { ...mainFields, ...connectionResourceFields }
}

const getContentAndPropertyValues = ({
  dirtyFields,
  initialValues,
  values,
  template,
}) => {
  const _values = Object.keys(dirtyFields).reduce((acc, dirtyField) => {
    // additional validation of empty dirtyFields(existing value deleting case)
    const field = getFieldByFormKey({
      formKey: dirtyField,
      templateFields: template?.fields,
    })
    if (
      !field ||
      field.type === FieldType.Boolean ||
      field.type === FieldType.Number
    )
      return acc
    if (initialValues[dirtyField] && !values[dirtyField]) {
      if (field.list) {
        acc[field.key] = []
      } else {
        if (field.resourceType === ResourceType.Property) {
          acc[field.key] = null
        } else if (field.resourceType === ResourceType.Content) {
          acc[field.key] = ''
        }
      }
    }

    return acc
  }, values ?? {})

  return Object.keys(_values).reduce(
    (acc, key) => {
      const field = getFieldByFormKey({
        formKey: removeFallbackAffix(key),
        templateFields: template?.fields,
      })

      if (
        field?.resourceType === ResourceType.Content ||
        // service field
        key === SHOULD_BE_APPROVED_KEY ||
        key === SHOULD_CREATE_INLINE_IMAGES_KEY ||
        key === 'verify-focus-keyword'
      ) {
        acc.contentValues[key] = _values[key]
      } else if (field?.resourceType === ResourceType.Property) {
        acc.propertyValues[key] = _values[key]
      }

      return acc
    },
    { contentValues: {}, propertyValues: {} },
  )
}

export const shouldAutoSuspendProviderOnClose = ({
  template,
  closedValue,
  wasClosed,
  wasSuspended,
}) => {
  if (template?.type !== TemplateType.Provider) return false

  return closedValue === true && !wasClosed && !wasSuspended
}

export const applyProviderClosedSuspendedRules = ({
  template,
  values,
  initialValues,
  propertyValues,
  dirtyFields,
  properties,
  networkId,
  verticalId,
}) => {
  const hasClosedField = !!getFieldByKey({
    key: CLOSED_KEY,
    fields: template?.fields,
  })
  const hasSuspendedField = !!getFieldByKey({
    key: SUSPENDED_KEY,
    fields: template?.fields,
  })
  if (!hasClosedField || !hasSuspendedField) return

  const wasClosed =
    get(initialValues, CLOSED_KEY) ??
    getPropertyValue({
      key: CLOSED_KEY,
      properties,
      networkId,
      verticalId,
    }) ??
    false
  const wasSuspended =
    get(initialValues, SUSPENDED_KEY) ??
    getPropertyValue({
      key: SUSPENDED_KEY,
      properties,
      networkId,
      verticalId,
    }) ??
    false

  if (
    !shouldAutoSuspendProviderOnClose({
      template,
      closedValue: get(values, CLOSED_KEY),
      wasClosed,
      wasSuspended,
    })
  ) {
    return
  }

  propertyValues[SUSPENDED_KEY] = true
  dirtyFields[SUSPENDED_KEY] = true
}

export const valuesToGraphqlData = ({
  values,
  form,
  template,
  currentNetwork,
  currentVertical,
  currentSite,
  currentLanguage,
  data,
  connectionResourcesData = [],
}: IValuesToGraphqlData): IValuesToGraphqlDataReturn => {
  // Return function pre-populated with data
  return async () => {
    const formState = form.getState()
    let _dirtyFields = { ...formState.dirtyFields }

    if (Object.keys(values).includes('verify-focus-keyword')) {
      _dirtyFields = { 'focus-keyword': true }
    }

    const { contentValues, propertyValues } = getContentAndPropertyValues({
      values,
      template,
      dirtyFields: _dirtyFields,
      initialValues: formState.initialValues,
    })

    const hasPublishedField = !!getFieldByKey({
      key: PUBLISHED_KEY,
      fields: template?.fields,
    })
    const duplicateOfFormValue = get(values, DUPLICATE_OF_KEY)
    const hasDuplicateOfProperty =
      typeof duplicateOfFormValue !== 'undefined'
        ? !!duplicateOfFormValue
        : !!getPropertyValue({
            key: DUPLICATE_OF_KEY,
            properties: data?.properties,
            networkId: currentNetwork?.id,
            companyId: currentNetwork?.companyId,
            verticalId: currentVertical?.id,
          })

    if (hasPublishedField && hasDuplicateOfProperty) {
      propertyValues[PUBLISHED_KEY] = false
    }

    applyProviderClosedSuspendedRules({
      template,
      values,
      initialValues: formState.initialValues,
      propertyValues,
      dirtyFields: _dirtyFields,
      properties: data?.properties,
      networkId: currentNetwork?.id,
      verticalId: currentVertical?.id,
    })

    const properties = await prepareProperties({
      values: propertyValues,
      template,
      dirtyFields: _dirtyFields,
      currentNetwork,
      currentVertical,
      currentSite,
    })

    const content = prepareContent({
      values: contentValues,
      template,
      currentLanguage,
      dirtyFields: _dirtyFields,
      data,
    })

    const current = {
      network: currentNetwork?.id,
      vertical: currentVertical?.id,
      site: currentSite?.id,
    }

    const resourcesToConnect = []
    for await (const connectionResourceData of connectionResourcesData) {
      const connectedResourcesIds = []
      Object.keys(formState.dirtyFields).forEach(key => {
        if (
          key.startsWith(
            `${CONNECTION_RESOURCES_KEY}.${connectionResourceData?.template?.type}`,
          )
        ) {
          const resourceId = key.split('.')[2]
          if (!connectedResourcesIds.includes(resourceId))
            connectedResourcesIds.push(resourceId)
        }
      })

      if (connectedResourcesIds.length) {
        // TODO: handle deleting
        for (const id of connectedResourcesIds) {
          let existingResource = null
          if (
            connectionResourceData?.template?.type ===
            TemplateType.Providertaxonomyconnection
          ) {
            existingResource = connectionResourceData.resources?.find(
              r => r.taxonomyItem?.id === id || r.taxonomyItemId === id,
            )
          }

          const _values =
            values[CONNECTION_RESOURCES_KEY][
              connectionResourceData?.template?.type
            ][id] || {}

          const _dirtyFields = {}
          Object.keys(formState.dirtyFields).forEach(key => {
            if (
              key.startsWith(
                `${CONNECTION_RESOURCES_KEY}.${connectionResourceData?.template?.type}.${id}`,
              )
            ) {
              const templateFieldKey = key.split('.')[3]
              _dirtyFields[templateFieldKey] = formState.dirtyFields[key]
            }
          })

          const _initialValues =
            formState.initialValues[CONNECTION_RESOURCES_KEY][
              connectionResourceData?.template?.type
            ][id] || {}

          let actionType = 'upsert'

          const isDelete =
            existingResource?.id &&
            Object.keys(_initialValues).length &&
            !Object.keys(_values).length
          if (isDelete) {
            actionType = 'delete'
            resourcesToConnect.push({
              id: existingResource?.id,
              current,
              templateType: connectionResourceData?.template?.type,
              actionType,
            })
            continue
          }
          const { contentValues, propertyValues } = getContentAndPropertyValues(
            {
              values: _values,
              dirtyFields: _dirtyFields,
              initialValues: _initialValues,
              template: connectionResourceData?.template,
            },
          )
          const properties = await prepareProperties({
            values: propertyValues,
            template: connectionResourceData?.template,
            dirtyFields: _dirtyFields,
            currentNetwork,
            currentVertical,
            currentSite,
          })

          const content = prepareContent({
            values: {
              ...contentValues,
              [SHOULD_BE_APPROVED_KEY]: values[SHOULD_BE_APPROVED_KEY],
              [SHOULD_CREATE_INLINE_IMAGES_KEY]:
                values[SHOULD_CREATE_INLINE_IMAGES_KEY],
            },
            template: connectionResourceData.template,
            currentLanguage,
            dirtyFields: _dirtyFields,
            data: existingResource,
          })

          resourcesToConnect.push({
            id: existingResource?.id || '',
            current,
            connectedResourceId: id,
            content,
            properties,
            templateType: connectionResourceData.template.type,
            actionType,
          })
        }
      }
    }

    return {
      current,
      properties,
      content,
      resourcesToConnect,
    }
  }
}

async function prepareProperties({
  values,
  template,
  dirtyFields,
  currentNetwork,
  currentVertical,
  currentSite,
}: IPrepareProperties): Promise<IPreparePropertiesReturn> {
  const items = []
  for await (const key of Object.keys(values ?? {})) {
    const fallbackField = isFallbackField(key)
    const templateKey = removeFallbackAffix(key)
    const field = getFieldByKey({ key: templateKey, fields: template?.fields })
    const value = get(values, key)

    // Only include correct fields
    if (typeof value === 'undefined') continue
    // Only include dirty fields
    if (
      !dirtyFields[key] &&
      // exceptions
      field.type !== FieldType.Pool &&
      field.key !== PUBLISHED_KEY
    )
      continue

    let parsedValue = value
    if (typeof value === 'function') {
      parsedValue = await value()
    } else if (
      value &&
      typeof value === 'object' &&
      typeof value._save === 'function'
    ) {
      // Handle LmsLinkInput pattern: { slug, _save, _version }
      parsedValue = await value._save()
    }

    let context = getContextId({
      field,
      currentNetwork,
      currentVertical,
      currentSite,
    })

    if (fallbackField) {
      context = currentNetwork?.id
    }

    /* A site-scoped field with no site selected has nowhere valid to go —
     * writing it at the network would apply it to every site at once. */
    if (field.context === ContextType.Site && !context) continue

    items.push({
      key: templateKey,
      data: parsedValue,
      context,
    })
  }

  return {
    items,
  }
}

function prepareContent({
  values,
  template,
  currentLanguage,
  dirtyFields,
  data,
}: IPrepareContent): IPrepareContentReturn {
  return {
    language: {
      id: currentLanguage.id,
      languageTag: currentLanguage.languageTag,
    },
    [SHOULD_BE_APPROVED_KEY]: values[SHOULD_BE_APPROVED_KEY],
    items: Object.keys(values ?? {}).reduce((acc, key) => {
      let templateFieldKey = getFieldKeyByFormKey(key)
      const field = getFieldByKey({
        key: templateFieldKey,
        fields: template?.fields,
      })

      if (!field) return acc
      if (
        field.data?.contentVariant &&
        acc.some(item => item.key === templateFieldKey)
      )
        return acc

      // approve fields in draft mode
      let overrideDirtyCheck = false
      if (values[SHOULD_BE_APPROVED_KEY] && !values['verify-focus-keyword']) {
        if (data?.content?.some(contentItem => contentItem.strings)) {
          // for backward compatibility with ContentItemV1
          const contentItem = data?.content?.find(
            contentItem => contentItem.key === templateFieldKey,
          )
          overrideDirtyCheck = hasDraftString(contentItem?.strings)
        } else {
          const contentItems = data?.content?.filter(
            contentItem => contentItem.key === templateFieldKey,
          )
          overrideDirtyCheck = hasDraftString(contentItems)
        }
      }

      const parsedValues =
        getFieldValueFromForm({
          field,
          dirtyFields,
          values,
          overrideDirtyCheck,
        }) || []

      parsedValues.forEach(item => {
        const _data = Array.isArray(item.data)
          ? item.data.filter(i => typeof i !== 'undefined' && i !== null)
          : item.data
        acc.push({
          key: item.key,
          data: _data,
          variant: item.variant,
          translation: StringItemTranslationType.Human,
          [SHOULD_CREATE_INLINE_IMAGES_KEY]:
            values[SHOULD_CREATE_INLINE_IMAGES_KEY],
        })
      })

      return acc
    }, []),
  }
}

const getFieldValueFromForm = ({
  field,
  dirtyFields,
  values,
  overrideDirtyCheck,
}: IGetFieldValueFromForm): IGetFieldValueFromFormReturn[] => {
  try {
    if (field.data?.contentVariant) {
      // get all dirty variant fields keys
      const dirtyFieldWithVariantsKeyArr = Object.keys(dirtyFields).filter(
        key => key.startsWith(field.key) && isContentVariantFormField(key),
      )

      // Only include dirty fields
      if (!overrideDirtyCheck && !dirtyFieldWithVariantsKeyArr.length) {
        return
      }

      if (overrideDirtyCheck) {
        // get rest of variants
        const fieldWithVariantsKeyArr = Object.keys(values).filter(
          key => key.startsWith(field.key) && isContentVariantFormField(key),
        )
        fieldWithVariantsKeyArr.forEach(key => {
          if (!dirtyFieldWithVariantsKeyArr.some(k => k === key)) {
            dirtyFieldWithVariantsKeyArr.push(key)
          }
        })
      }

      // get array with parsed each dirty variant field
      return dirtyFieldWithVariantsKeyArr.map(valueKey => {
        const variant = getContentVariantOfFormField(valueKey)

        return {
          key: field.key,
          data: get(values, valueKey),
          variant,
        }
      })
    } else {
      // Only include dirty fields
      if (!overrideDirtyCheck && !dirtyFields[field.key]) {
        return
      }

      const data = get(values, field.key)

      if (field.key === 'focus-keyword' && data === '') {
        return
      }

      return [
        {
          key: field.key,
          data,
          variant: 0,
        },
      ]
    }
  } catch (error) {
    throw new Error(
      `Editor: Unable to parse content with key ${field.key} and type: ${field.type}`,
    )
  }
}

export const addContentVariantAffix = ({
  key,
  variant,
}: IAddContentVariantAffix): string => {
  return `${key}${CONTENT_VARIANT_AFFIX}${variant}`
}

export const removeContentVariantAffix = (key: string): string => {
  try {
    return key.split(CONTENT_VARIANT_AFFIX)[0]
  } catch {
    console.error(CONTENT_VARIANT_ERROR_TEXT)
  }
}

export const isContentVariantFormField = (key: string): boolean => {
  try {
    const variant = key.split(CONTENT_VARIANT_AFFIX)[1]
    return variant !== undefined
  } catch {
    console.error(CONTENT_VARIANT_ERROR_TEXT)
  }
}

export const getContentVariantOfFormField = (key: string): number => {
  try {
    // example: key === 'content-block_v1'

    // return v1
    const variant = key.split(CONTENT_VARIANT_AFFIX)[1]
    // return 1 || 0
    return variant ? Number(variant) : 0
  } catch {
    console.error(CONTENT_VARIANT_ERROR_TEXT)
  }
}

export const addFallbackAffix = (key: string): string => {
  return `${key}${FALLBACK_AFFIX}`
}

export const removeFallbackAffix = (key: string): string => {
  try {
    return key.split(FALLBACK_AFFIX)[0]
  } catch {
    console.error(FALLBACK_ERROR_TEXT)
  }
}

export const isFallbackField = (key: string): boolean => {
  return key?.includes(FALLBACK_AFFIX)
}

export const filterResourceContentByLanguageOld = ({
  resource,
  currentLanguageTag,
}: IFilterResourceContentByLanguage) => {
  if (!resource) return

  const content = (resource.content ?? []).map(contentItem => {
    let languageTag = currentLanguageTag
    const isNoTranslateField =
      contentItem.translationType === ContentItemTranslationType.Notranslate
    if (isNoTranslateField) {
      languageTag = DEFAULT_LANGUAGE.languageTag
    }

    let strings = (contentItem.strings ?? []).filter(stringItem => {
      return stringItem.language?.languageTag === languageTag
    })

    if (
      isNoTranslateField &&
      currentLanguageTag !== DEFAULT_LANGUAGE.languageTag &&
      strings[0]
    ) {
      // imitating notranslate field as always approved for non eng sites
      const _string = { ...strings[0], status: StringItemStatus.Approved }
      strings = [_string]
    }

    return {
      ...contentItem,
      strings,
    }
  })

  return {
    ...resource,
    content,
  }
}

export const filterResourceContentByLanguage = ({
  resource,
  currentLanguageTag,
}: IFilterResourceContentByLanguage) => {
  if (!resource) return

  // for backward compatibility with ContentItemV1
  if (resource.content.some(contentItem => contentItem.strings)) {
    return filterResourceContentByLanguageOld({
      resource,
      currentLanguageTag,
    })
  }

  const content = resource.content.filter(contentItem => {
    let languageTag = currentLanguageTag
    const isNoTranslateField =
      contentItem.translationType === ContentItemTranslationType.Notranslate
    if (isNoTranslateField) {
      languageTag = DEFAULT_LANGUAGE.languageTag
    }

    return contentItem.language?.languageTag === languageTag
  })

  return {
    ...resource,
    content,
  }
}

export const getPropertyValueOfName = ({
  name,
  values,
  template,
}: IGetPropertyValueOfName): string | boolean | undefined => {
  const field = getFieldByKey({ key: name, fields: template?.fields })
  if (!field) {
    return
  }

  try {
    return get(values, name)
  } catch (error) {
    // Don't do anything
  }
}

export const getFieldKeyByFormKey = (formKey: string): string => {
  return isContentVariantFormField(formKey)
    ? removeContentVariantAffix(formKey)
    : formKey
}

export const getFieldByFormKey = ({
  formKey,
  templateFields,
}: IGetFieldByFormKey): TemplateField | undefined => {
  if (!formKey || !templateFields) return

  let templateFieldKey = getFieldKeyByFormKey(formKey)
  return getFieldByKey({ key: templateFieldKey, fields: templateFields })
}

export const fieldFormatIdentity = (value): typeof value => value
